"use client";

import Image from "next/image";
import { PortableText } from "@portabletext/react";
import type { CSSProperties, ReactNode } from "react";
import { GROQButton } from "@/sanity/lib/definitions";
import type { GROQImage } from "@/sanity/lib/definitions";
import { PortableTextBlock } from "sanity";
import { richTextComponent } from "@/sanity/sanityPortableText";
import { CTAButton } from "@/shadcn/ui/button";
import { FadeIn } from "@/components/global/utils/FadeIn";
import { MobileCarousel } from "@/components/global/utils/MobileCarousel";
import { cn } from "@/lib/utils";
import { PipeChip } from "./_shared";

interface MentorsSectionProps {
  chip?: string;
  title?: PortableTextBlock[];
  mentors?: MentorItem[];
  cta?: GROQButton;
  variant?: "home" | "community" | "courseGallery";
  desktopHeader?: ReactNode;
  mobileHeader?: ReactNode;
  mobileFooter?: ReactNode;
  mobileItemLimit?: number;
}

export interface MentorItem {
  image?: GROQImage;
  name?: string;
  title?: string | PortableTextBlock[];
  imagePosition?: "top" | "center" | "bottom";
  localImage?: string;
  crop?: {
    height: string;
    left: string;
    top: string;
    width: string;
  };
  overlay?: boolean;
}

function MentorCard({ mentor }: { mentor: MentorItem }) {
  const source = mentor.image?.src ?? mentor.localImage;
  const position = mentor.imagePosition ?? "center";

  return (
    <div className="flex w-full flex-col gap-[2rem]">
      <div className="relative aspect-[390/480] w-[min(48.75rem,100%)] overflow-hidden rounded-[1.5rem] bg-white/[0.04]">
        {source && mentor.crop ? (
          <Image
            src={source}
            alt={mentor.name ?? ""}
            width={1600}
            height={1600}
            quality={100}
            sizes="(min-width: 1024px) 390px, 85vw"
            placeholder={mentor.image?.lqip ? "blur" : undefined}
            blurDataURL={mentor.image?.lqip}
            className="absolute max-w-none"
            style={mentor.crop as CSSProperties}
          />
        ) : source ? (
          <Image
            src={source}
            alt={mentor.name ?? ""}
            fill
            quality={100}
            sizes="(min-width: 1024px) 390px, 85vw"
            placeholder={mentor.image?.lqip ? "blur" : undefined}
            blurDataURL={mentor.image?.lqip}
            className={cn(
              "object-cover",
              position === "top" && "object-top",
              position === "center" && "object-center",
              position === "bottom" && "object-bottom",
            )}
          />
        ) : (
          <div className="absolute inset-0 bg-gradient-to-b from-white/10 to-white/[0.03]" />
        )}
        {mentor.overlay && (
          <span className="pointer-events-none absolute inset-0 bg-black/10" />
        )}
      </div>
      <div className="flex flex-col gap-[1rem]">
        <p className="m-0 font-news text-[3rem] leading-none tracking-[-0.055rem] text-white">
          {mentor.name}
        </p>
        {Array.isArray(mentor.title) ? (
          <div className="richtext font-geist text-[2rem] leading-none tracking-[0.03rem] text-white/50 prose-p:m-0 prose-p:text-inherit prose-p:leading-inherit prose-headings:m-0 prose-headings:text-inherit prose-headings:leading-inherit">
            <PortableText
              value={mentor.title}
              components={richTextComponent}
            />
          </div>
        ) : mentor.title ? (
          <p className="m-0 font-geist text-[2rem] leading-none tracking-[0.03rem] text-white/50">
            {mentor.title}
          </p>
        ) : null}
      </div>
    </div>
  );
}

export default function MentorsSection({
  chip,
  title,
  mentors,
  cta,
  variant = "home",
  desktopHeader,
  mobileHeader,
  mobileFooter,
  mobileItemLimit,
}: MentorsSectionProps) {
  const items = mentors ?? [];
  const isCommunity = variant === "community";
  const isCourseGallery = variant === "courseGallery";
  const mobileItems = mobileItemLimit ? items.slice(0, mobileItemLimit) : items;

  return (
    <section
      id={isCommunity ? "mentors" : undefined}
      className={cn(
        "flex w-full flex-col items-center gap-[3.5rem] bg-black px-[2rem] lg:px-0",
        isCommunity ? "lg:gap-[5rem]" : "lg:gap-[7rem]",
        isCourseGallery
          ? "pt-[8rem] lg:pt-[15rem]"
          : isCommunity
            ? "pb-[8rem] lg:pb-[18.75rem]"
            : "pb-[8rem] lg:pb-[17.5rem]",
      )}
    >
      {isCommunity ? (
        <>
          {mobileHeader}
          {desktopHeader}
        </>
      ) : (
        <div className="flex flex-col items-center text-center">
          {chip && (
            <FadeIn delay={0}>
              <PipeChip>{chip}</PipeChip>
            </FadeIn>
          )}
          {title && (
            <FadeIn
              delay={0.1}
              className="richtext richtext-center w-[98.375rem] max-w-full prose-p:font-news prose-p:text-[6rem] prose-p:leading-[1.1] prose-p:tracking-[-0.25rem] prose-p:text-white/90 prose-em:font-news prose-em:italic prose-headings:font-news prose-headings:text-[4.5rem] prose-headings:leading-[1.1] prose-headings:tracking-[-0.25rem] prose-headings:text-white/90 lg:prose-headings:text-[6rem]"
            >
              <PortableText value={title} components={richTextComponent} />
            </FadeIn>
          )}
        </div>
      )}

      {/* Desktop — 3-column grid */}
      <div
        className={cn(
          "hidden max-w-full gap-x-[1.875rem] gap-y-[7rem] lg:grid lg:grid-cols-3",
          isCommunity ? "w-[min(150rem,100%)]" : "w-[min(135.25rem,100%)]",
        )}>
        {items.map((m, i) => (
          <FadeIn key={i} delay={(i % 3) * 0.1}>
            <MentorCard mentor={m} />
          </FadeIn>
        ))}
      </div>

      {/* Mobile — carousel with arrows + stepper */}
      {mobileItems.length > 0 && (
        <MobileCarousel variant={isCommunity ? "communityMentors" : "default"}>
          {mobileItems.map((m, i) => (
            <MentorCard key={i} mentor={m} />
          ))}
        </MobileCarousel>
      )}

      {isCommunity ? (
        mobileFooter
      ) : !isCourseGallery ? (
        <FadeIn className="w-full lg:w-fit lg:px-0">
          <CTAButton data={cta ?? {}} />
        </FadeIn>
      ) : null}
    </section>
  );
}
